import React, { useEffect, useState } from 'react'
import {
  Box, Card, CardActionArea, CardContent, Chip, Typography,
} from '@mui/material'
import CheckCircleIcon          from '@mui/icons-material/CheckCircle'
import LockIcon                 from '@mui/icons-material/Lock'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import { useNavigate }          from 'react-router-dom'
import { supabase }             from '../../supabaseClient'
import { Project, Step }        from '../../types'

// Shared "Research Roadmap" — the canonical visual representation of the
// 8-step scientific method / engineering design curriculum. Rendered on
// the student dashboard AND at the top of every /step/N page so the
// student sees the full journey wherever they are.
//
// Pedagogical principle (per the team): the roadmap is part of the
// curriculum, not navigation chrome. Each step shows its full name and
// description so students re-encounter the framing on every page load.

// ── Step labels + descriptions (lifted intact from dashboard) ────────────────

const STEP_LABELS_SCIENCE: Record<number, string> = {
  1: 'Select a Research Topic',
  2: 'Ask a Research Question',
  3: 'Gather Background Information',
  4: 'Formulate a Hypothesis',
  5: 'Design an Experiment',
  6: 'Conduct the Experiment and Record Data',
  7: 'Analyze Data and Draw Conclusions',
  8: 'Present Your Findings',
}

const STEP_LABELS_ENGINEERING: Record<number, string> = {
  1: 'Select a Research Topic',
  2: 'Identify a Problem',
  3: 'Gather Background Information',
  4: 'Specify Design Requirements',
  5: 'Design a Solution',
  6: 'Develop the Solution',
  7: 'Test and Record Performance',
  8: 'Present Your Solutions',
}

const STEP_DESCRIPTIONS_SCIENCE: Record<number, string> = {
  1: 'Choose a phenomenon in the natural world you are curious about — a good topic is observable, measurable, and connected to a broader field of knowledge',
  2: 'Define the specific relationship you want to investigate — your question must identify the variables involved and be answerable through empirical evidence',
  3: 'Survey existing knowledge so your work builds on established science and does not duplicate what is already known',
  4: 'Make an evidence-based, testable prediction — state the expected relationship between your independent and dependent variables based on your research',
  5: 'Plan a controlled procedure that isolates variables and eliminates bias — a good experimental design is repeatable and produces measurable data',
  6: 'Execute your experiment exactly as designed and record observations and measurements systematically — accurate records are the foundation of scientific integrity',
  7: 'Examine your data for patterns and determine whether the hypothesis is supported and what the findings mean for the broader field',
  8: 'Share your methodology, data, and conclusions so others can evaluate, replicate, and build on your work',
}

const STEP_DESCRIPTIONS_ENGINEERING: Record<number, string> = {
  1: 'Identify a domain where human needs are unmet or existing solutions fall short — the topic defines the space in which the problem lives',
  2: 'Precisely define the problem, the end user, and the context — a well-defined problem is the most critical step, solving the wrong problem wastes every step that follows',
  3: 'Research existing solutions, relevant science, available materials, and known constraints — this prevents reinventing the wheel and informs smarter design decisions',
  4: 'Define what the solution must do and what constraints it must operate within — performance targets, cost, size, and safety become the measurable criteria for success',
  5: 'Translate requirements into a concrete plan — sketches, schematics, or specifications — multiple designs are often considered before selecting the most promising approach',
  6: 'Build the actual product, code, or system — this is where plans meet reality and unforeseen challenges emerge',
  7: 'Test the solution against the design requirements, record results objectively, and when performance falls short, identify root causes and iterate',
  8: 'Document the design process, test results, and recommendations — professional communication of engineering work is as important as the solution itself',
}

interface SubmissionSummary {
  step_id:          string
  score:            number | null
  advance_decision: string | null
}

interface Props {
  project:         Project
  /** Reserved for future per-student-edit affordances; currently unused. */
  viewerIsStudent: boolean
  /** Path prefixer (no-op for normal sessions; prepends /view-as/<id> for staff mirrors). */
  prefixPath:      (p: string) => string
  /** Trial students stay locked at Step 1. */
  isTrial?:        boolean
}

export default function StepRoadmap({ project, viewerIsStudent: _viewerIsStudent, prefixPath, isTrial }: Props) {
  const navigate = useNavigate()

  const [steps,   setSteps]   = useState<Step[]>([])
  const [scores,  setScores]  = useState<SubmissionSummary[]>([])

  const trackConfirmed = !!project.track
  const isEngineering  = project.track === 'engineering_design'
  const STEP_LABELS    = isEngineering ? STEP_LABELS_ENGINEERING       : STEP_LABELS_SCIENCE
  const STEP_DESCS     = isEngineering ? STEP_DESCRIPTIONS_ENGINEERING : STEP_DESCRIPTIONS_SCIENCE
  const currentStep    = project.current_step ?? 1

  useEffect(() => {
    let cancelled = false
    async function load() {
      const [{ data: stepRows }, { data: subs }] = await Promise.all([
        supabase.from('step').select('*').order('step_number'),
        supabase.from('submission')
          .select('step_id, score, advance_decision')
          .eq('project_id', project.project_id)
          .order('version_number', { ascending: false }),
      ])
      if (cancelled) return

      // Track-filter steps; deduplicate on step_number (some seeds had dupes)
      const trackSteps = (stepRows ?? []).filter((s: Step) =>
        !project.track || s.track === project.track,
      )
      const unique: Step[] = []
      const seen = new Set<number>()
      for (const s of trackSteps) {
        if (!seen.has(s.step_number)) { unique.push(s); seen.add(s.step_number) }
      }
      setSteps(unique)

      // Latest submission per step
      const latest: SubmissionSummary[] = []
      const seenStep = new Set<string>()
      for (const sub of subs ?? []) {
        if (!seenStep.has(sub.step_id)) { latest.push(sub); seenStep.add(sub.step_id) }
      }
      setScores(latest)
    }
    load()
    return () => { cancelled = true }
  }, [project.project_id, project.track])

  function stepStatus(num: number): 'complete' | 'current' | 'locked' {
    if (isTrial && num > 1) return 'locked'
    if (num < currentStep)  return 'complete'
    if (num === currentStep) return 'current'
    return 'locked'
  }
  function stepScore(step: Step): number | null {
    return scores.find(s => s.step_id === step.step_id)?.score ?? null
  }

  // ── Pre-branch view: track not yet confirmed ───────────────────────────────
  // Shows Step 1 as current (the work in progress), then both track columns
  // for Steps 2-8 so the student sees both options before committing.
  if (!trackConfirmed) {
    const step1 = steps.find(s => s.step_number === 1)
    const hasStartedStep1 = step1 ? scores.some(s => s.step_id === step1.step_id) : false
    return (
      <Box>
        <SectionLabel />
        {!hasStartedStep1 && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, lineHeight: 1.7 }}>
            Complete Step 1 to choose your research track. Both paths follow the same rigor —
            the difference is whether you are <strong>explaining</strong> a natural phenomenon
            (Scientific Method) or <strong>building</strong> a solution to a problem (Engineering Design).
          </Typography>
        )}

        {/* Step 1 — current */}
        {step1 && (
          <Box sx={{ mb: 2 }}>
            <StepRow
              num={1}
              name={STEP_LABELS[1]}
              desc={STEP_DESCS[1]}
              status={stepStatus(1)}
              score={stepScore(step1)}
              onClick={() => navigate(prefixPath('/step/1'))}
            />
          </Box>
        )}

        {/* Steps 2–8 in both-tracks-side-by-side preview */}
        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
          {[
            { label: 'Scientific Method',  labels: STEP_LABELS_SCIENCE,     descs: STEP_DESCRIPTIONS_SCIENCE },
            { label: 'Engineering Design', labels: STEP_LABELS_ENGINEERING, descs: STEP_DESCRIPTIONS_ENGINEERING },
          ].map(col => (
            <Box key={col.label} sx={{ flex: 1, border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
              <Box sx={{ px: 2, py: 1.5, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>
                  {col.label}
                </Typography>
              </Box>
              {Array.from({ length: 7 }, (_, i) => i + 2).map((num, idx) => (
                <Box
                  key={num}
                  sx={{
                    px: 2, py: 1.25,
                    borderTop: idx === 0 ? 'none' : '1px solid',
                    borderColor: 'divider',
                    opacity: 0.65,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                    <LockIcon sx={{ fontSize: 14, color: 'text.disabled', mt: '3px', flexShrink: 0 }} />
                    <Box>
                      <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, lineHeight: 1.4 }}>
                        {num}. {col.labels[num]}
                      </Typography>
                      <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', lineHeight: 1.5, mt: 0.25 }}>
                        {col.descs[num]}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          ))}
        </Box>

      </Box>
    )
  }

  // ── Post-branch view: track confirmed, single unified list ─────────────────
  // Step 1 is part of the same list as Steps 2-8. Track is shown as a chip
  // on the Step 1 row.
  const trackLabel = isEngineering ? 'Engineering Design' : 'Scientific Method'

  return (
    <Box>
      <SectionLabel />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {steps.map(step => (
          <StepRow
            key={step.step_id}
            num={step.step_number}
            name={STEP_LABELS[step.step_number] ?? step.step_name}
            desc={STEP_DESCS[step.step_number] ?? step.description}
            status={stepStatus(step.step_number)}
            score={stepScore(step)}
            trackChip={step.step_number === 1 ? trackLabel : null}
            onClick={() => navigate(prefixPath(`/step/${step.step_number}`))}
          />
        ))}
      </Box>
    </Box>
  )
}

// ── Sub-components ───────────────────────────────────────────────────────────

function SectionLabel() {
  return (
    <Typography
      variant="subtitle2"
      sx={{ fontWeight: 700, mb: 1.5, color: 'text.secondary', letterSpacing: '0.06em', textTransform: 'uppercase', fontSize: '0.72rem' }}
    >
      Research Roadmap
    </Typography>
  )
}

function StepRow({
  num, name, desc, status, score, trackChip, onClick,
}: {
  num:            number
  name:           string
  desc:           string
  status:         'complete' | 'current' | 'locked'
  score:          number | null
  trackChip?:     string | null
  onClick:        () => void
}) {
  return (
    <Card
      variant="outlined"
      sx={{
        opacity:     status === 'locked' ? 0.45 : 1,
        borderColor: status === 'current' ? 'primary.main' : status === 'complete' ? 'success.light' : 'divider',
        borderWidth: status === 'current' ? 2 : 1,
        bgcolor:     status === 'complete' ? 'rgba(76,175,80,0.04)' : 'background.paper',
      }}
    >
      <CardActionArea disabled={status === 'locked'} onClick={onClick}>
        <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5, px: 2 }}>
          <Box sx={{
            color:     status === 'complete' ? 'success.main' : status === 'current' ? 'primary.main' : 'text.disabled',
            flexShrink: 0,
          }}>
            {status === 'complete' ? <CheckCircleIcon sx={{ fontSize: 20 }} /> :
             status === 'locked'   ? <LockIcon sx={{ fontSize: 20 }} /> :
             <RadioButtonUncheckedIcon sx={{ fontSize: 20 }} />}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
              Step {num}: {name}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
              {desc}
            </Typography>
          </Box>
          {trackChip && status === 'complete' && (
            <Chip label={trackChip} size="small" color="success" variant="outlined" />
          )}
          {score !== null && (
            <Chip label={`${score}/100`} size="small" color={score >= 85 ? 'success' : 'warning'} />
          )}
          {status === 'current' && score === null && (
            <Chip label="Continue" size="small" color="primary" />
          )}
        </CardContent>
      </CardActionArea>
    </Card>
  )
}
